/*
 * Jobs: the list, one job's page (details, line items, crew notes in English and Spanish, visits, notes) and the job form
 * (Repeats choice, line items with their own repeat, crew, crew notes, and "Starting when" for new prices on a repeating job).
 */
// Jobs: the list.
import { addDays, type JobRow } from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { type JobsQuery, useJobPages, useJobs } from "../../api/hooks";
import { useSession } from "../../app/session";
import { DataTable, MoreRows } from "../../components/DataTable";
import { clock, money, shortDate, todayIn } from "../../components/format";
import { Button, Chip, EmptyState, LoadError, Page, StatusLabel } from "../../components/parts";
import { CrewChips } from "../../components/work";

export function JobsList() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const [q, setQ] = useState<JobsQuery>({ status: "active", kind: "all" });
  const [typed, setTyped] = useState("");
  const jobs = useJobPages(company.id, { ...q, q: typed.trim() || undefined });
  const all = useJobs(company.id, { status: "all" });
  const rows = jobs.data?.pages.flatMap((p) => p.items) ?? [];
  const moreToLoad = jobs.hasNextPage;
  const loadMore = () => {
    if (jobs.hasNextPage && !jobs.isFetchingNextPage) void jobs.fetchNextPage();
  };
  const every = all.data?.items ?? [];
  const today = todayIn(company.timezone);
  const active = every.filter((j) => j.active);
  return (
    <Page
      title="Jobs"
      primaryAction={
        can("job.update") ? (
          <Button icon="plus" onClick={() => void navigate({ to: "/jobs/new", search: {} })}>
            New job
          </Button>
        ) : null
      }
    >
      <div className="tiles">
        <div className="tile">
          <span className="label">Active jobs</span>
          <span className="value">{all.data ? active.length : <span className="skeleton inline" />}</span>
          <span className="hint">{active.filter((j) => j.frequency !== "one-time").length} repeat</span>
        </div>
        <div className="tile">
          <span className="label">Value per visit round</span>
          <span className="value">{money(active.reduce((s, j) => s + j.visitTotalCents, 0))}</span>
          <span className="hint">Every active job once</span>
        </div>
        <div className="tile">
          <span className="label">No crew yet</span>
          <span className="value">{active.filter((j) => !j.crew.length).length}</span>
          <span className="hint">Active jobs nobody is on</span>
        </div>
        <div className="tile">
          <span className="label">Next 7 days</span>
          <span className="value">{active.filter((j) => j.nextVisit && j.nextVisit <= addDays(today, 6)).length}</span>
          <span className="hint">Jobs with a visit coming</span>
        </div>
      </div>
      <DataTable<JobRow>
        title="All jobs"
        rows={rows}
        rowKey={(j) => j.id}
        loading={jobs.isFetching && !jobs.isFetchingNextPage}
        error={jobs.error ? <LoadError error={jobs.error} retry={() => void jobs.refetch()} /> : null}
        query={{ value: typed, onChange: setTyped, placeholder: "Job, client or number" }}
        onRowClick={(j) => void navigate({ to: "/jobs/$id", params: { id: j.id } })}
        count={moreToLoad ? `${String(rows.length)} so far` : undefined}
        footer={moreToLoad ? <MoreRows loading={jobs.isFetchingNextPage} onMore={loadMore} /> : null}
        tools={
          <div className="chips">
            {(
              [
                ["active", "Active"],
                ["ended", "Ended"],
                ["all", "All"],
              ] as const
            ).map(([v, label]) => (
              <Chip key={v} pressed={q.status === v} onClick={() => setQ({ ...q, status: v })}>
                {label}
              </Chip>
            ))}
            <Chip
              pressed={q.kind === "repeating"}
              onClick={() => setQ({ ...q, kind: q.kind === "repeating" ? "all" : "repeating" })}
            >
              Repeating
            </Chip>
            <Chip
              pressed={q.kind === "one-time"}
              onClick={() => setQ({ ...q, kind: q.kind === "one-time" ? "all" : "one-time" })}
            >
              One time
            </Chip>
          </div>
        }
        empty={
          <EmptyState icon="jobs" title="No jobs yet">
            A job is the work for a client. Repeating jobs fill the schedule on their own.
          </EmptyState>
        }
        rowClass={(j) => (j.active ? undefined : "inactive")}
        columns={[
          {
            key: "number",
            header: "#",
            sort: (j) => j.number ?? 0,
            render: (j) => <span className="num muted">{j.number}</span>,
          },
          {
            key: "name",
            header: "Job",
            sort: (j) => j.clientName,
            render: (j) => (
              <div className="name-cell">
                <b>{j.clientName}</b>
                <span className="muted">{j.name}</span>
              </div>
            ),
          },
          { key: "often", header: "Repeats", render: (j) => j.frequencyLabel },
          {
            key: "next",
            header: "Next visit",
            sort: (j) => j.nextVisit ?? "9999",
            render: (j) =>
              j.completedOn ? (
                <StatusLabel status="success">Completed {shortDate(j.completedOn)}</StatusLabel>
              ) : j.active ? (
                j.nextVisit ? (
                  <span>
                    {shortDate(j.nextVisit)} <span className="muted">{clock(j.startTime, "")}</span>
                  </span>
                ) : (
                  <span className="muted">None</span>
                )
              ) : (
                <StatusLabel status="inactive">Ended {shortDate(j.endsOn)}</StatusLabel>
              ),
          },
          { key: "crew", header: "Crew", render: (j) => <CrewChips crew={j.crew} /> },
          {
            key: "value",
            header: "Per visit",
            align: "right",
            sort: (j) => j.visitTotalCents,
            render: (j) => <span className="num">{money(j.visitTotalCents)}</span>,
          },
        ]}
      />
    </Page>
  );
}

// One job ------------------------------------------------------------------------------------------------------------
